import React from 'react'
import { IonButton, IonFooter, IonIcon, IonSpinner } from '@ionic/react'
import { chevronBack, chevronForward } from 'ionicons/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  getPulsaraCurrentSubStep,
  getPulsaraCurrentStep,
  getPulsaraLoading,
  getPulsaraDetailsVitals,
  getPulsaraDetailsLabs,
  getPulsaraDetailsAttachmentList,
} from '../../selectors'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import {
  setCurrentStep,
  setCurrentSubStep,
  resetPulsara,
  importPulsaraData,
  handleResetsOnBack,
} from '../../store/pulsara.actions'
import {
  getNextNavigation,
  getBackButtonVisibility,
  getNextButtonVisibility,
  getImportButtonVisibility,
} from '../../utils/pulsaraNavigation.util'
import './PulsaraFooter.component.css'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { STEP_IDS, PATIENT_MATCH_SUB_STEP_IDS, IMPORT_SUB_STEP_IDS } from '../../constants'
import { resetSearchState } from '../../../patientSearch/store/patientSearch.actions'

const PulsaraFooter: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const currentStep = useSelector(getPulsaraCurrentStep)
  const currentSubStep = useSelector(getPulsaraCurrentSubStep)
  const loading = useSelector(getPulsaraLoading)
  const vitals = useSelector(getPulsaraDetailsVitals)
  const labs = useSelector(getPulsaraDetailsLabs)
  const attachmentList = useSelector(getPulsaraDetailsAttachmentList)
  const { FORCE_PT_SEARCH } = useSelector(getServiceConfigSelector)

  const hasVitalsOrLabs = vitals?.length > 0 || labs?.length > 0
  const hasAttachments = (attachmentList?.length ?? 0) > 0
  const importData = { hasVitalsOrLabs, hasAttachments }

  const isConflictScreen =
    currentStep === STEP_IDS.PATIENT_MATCH && currentSubStep === PATIENT_MATCH_SUB_STEP_IDS.PATIENT_CONFLICT
  const isVitalsLabsStep = currentStep === STEP_IDS.IMPORT && currentSubStep === IMPORT_SUB_STEP_IDS.VITALS_LABS
  const isConflictOrImport = isConflictScreen || currentStep === STEP_IDS.IMPORT
  const attachmentsLoading = loading === 'attachments' && isConflictOrImport
  const showSpinnerOnNext =
    (attachmentsLoading && !hasVitalsOrLabs && isConflictScreen) || (attachmentsLoading && isVitalsLabsStep)

  const handleCancel = () => {
    dispatch(resetPulsara())
    if (FORCE_PT_SEARCH === 1) {
      dispatch(resetSearchState())
    }
    history.push(TabConstants.CHART_ROUTE)
  }

  const handleBack = () => {
    dispatch(handleResetsOnBack())
  }

  const handleNext = () => {
    const navigation = getNextNavigation(currentStep, currentSubStep, importData)
    if (navigation) {
      dispatch(setCurrentStep(navigation.step))
      dispatch(setCurrentSubStep(navigation.subStep))
    }
  }

  const handleImport = () => {
    dispatch(importPulsaraData())
  }

  const showBack = getBackButtonVisibility(currentStep)
  const showNext = getNextButtonVisibility(currentStep, currentSubStep, importData)
  const showImport = getImportButtonVisibility(currentStep, currentSubStep, importData)

  const isPulsaraSearchStep = currentStep === STEP_IDS.PULSARA_SEARCH

  if (isPulsaraSearchStep) {
    return (
      <IonFooter className='pulsara-footer d-block'>
        <IonButton className='pulsara-footer-button my-auto' expand='block' onClick={handleCancel}>
          {t('common.buttons.Cancel')}
        </IonButton>
      </IonFooter>
    )
  }

  return (
    <IonFooter className='pulsara-footer'>
      <IonButton className='pulsara-footer-button' onClick={handleCancel}>
        {t('common.buttons.Cancel')}
      </IonButton>
      <div className='pulsara-footer-button-group'>
        {showBack && (
          <IonButton className='pulsara-footer-button' onClick={handleBack}>
            <IonIcon className='pulsara-footer-button-chevron' icon={chevronBack} />
            {t('common.buttons.Back')}
          </IonButton>
        )}
        {showSpinnerOnNext ? (
          <IonButton className='pulsara-footer-button-alt' disabled>
            <IonSpinner name='crescent' className='pulsara-footer-button-spinner' />
          </IonButton>
        ) : (
          <>
            {showNext && (
              <IonButton className='pulsara-footer-button-alt' onClick={handleNext}>
                {t('common.buttons.Next')}
                <IonIcon className='pulsara-footer-button-chevron' icon={chevronForward} />
              </IonButton>
            )}
            {showImport && (
              <IonButton className='pulsara-footer-button-alt' onClick={handleImport}>
                {t('common.buttons.Import')}
              </IonButton>
            )}
          </>
        )}
      </div>
    </IonFooter>
  )
}

export default PulsaraFooter
